import type { Security } from '../types/Security';
import { getSecurityIdentifierSearchText } from '../utils/getSecurityMetadata';
import { getSearchSymbol } from '../utils/security';
import { getMatchTier } from '../utils/substringMatch';

const SYMBOL_PRIORITY = 2;
const DESCRIPTION_PRIORITY = 1;

type SearchField = {
  priority: number;
  value: string;
};

type FieldRank = {
  matchIndex: number;
  priority: number;
  tier: number;
};

export type SecuritySearchRecord = {
  fields: readonly SearchField[];
  security: Security;
};

/** Higher field priority wins, then match quality, then earlier match position. */
function compareFieldRank(a: FieldRank, b: FieldRank): number {
  return b.priority - a.priority || a.tier - b.tier || a.matchIndex - b.matchIndex;
}

function pushField(fields: SearchField[], value: string | null | undefined, priority: number) {
  if (value == null || value === '') {
    return;
  }
  fields.push({ priority, value: value.toLowerCase() });
}

/**
 * Normalized fields for one security. Built once per list change so each keystroke
 * scans these strings instead of lowercasing symbols and rebuilding identifier text.
 */
export function createSecuritySearchRecord(
  security: Security,
  searchOnDescription: boolean,
  includeSedol: boolean
): SecuritySearchRecord {
  const fields: SearchField[] = [];
  pushField(fields, security.Symbol, SYMBOL_PRIORITY);
  pushField(fields, security.DisplaySymbol, SYMBOL_PRIORITY);
  pushField(fields, security.searchSymbol ?? getSearchSymbol(security.Symbol), SYMBOL_PRIORITY);
  pushField(fields, getSecurityIdentifierSearchText(security, includeSedol), SYMBOL_PRIORITY);
  if (searchOnDescription) {
    pushField(fields, security.Description, DESCRIPTION_PRIORITY);
  }
  return { fields, security };
}

function bestFieldRank(fields: readonly SearchField[], term: string): FieldRank | undefined {
  let best: FieldRank | undefined;
  for (const field of fields) {
    const matchIndex = field.value.indexOf(term);
    if (matchIndex < 0) {
      continue;
    }
    const candidate = { matchIndex, priority: field.priority, tier: getMatchTier(field.value, term, matchIndex) };
    if (best == null || compareFieldRank(candidate, best) < 0) {
      best = candidate;
    }
  }
  return best;
}

function sumRanks(ranks: readonly FieldRank[], pick: (rank: FieldRank) => number): number {
  let total = 0;
  for (const rank of ranks) {
    total += pick(rank);
  }
  return total;
}

/** Higher total field priority wins, then lower total tier, then earlier total match position. */
function compareAggregateRanks(a: readonly FieldRank[], b: readonly FieldRank[]): number {
  const priorityDelta = sumRanks(b, rank => rank.priority) - sumRanks(a, rank => rank.priority);
  if (priorityDelta !== 0) {
    return priorityDelta;
  }
  const tierDelta = sumRanks(a, rank => rank.tier) - sumRanks(b, rank => rank.tier);
  if (tierDelta !== 0) {
    return tierDelta;
  }
  return sumRanks(a, rank => rank.matchIndex) - sumRanks(b, rank => rank.matchIndex);
}

/**
 * One pass over indexed records. Every term has to match. Results are ordered by the combined
 * quality of each term's best field, and equal totals keep input order.
 */
export function searchSecurityRecords(records: readonly SecuritySearchRecord[], terms: readonly string[]): Security[] {
  if (terms.length === 0) {
    return [];
  }

  const matches: { ranks: FieldRank[]; security: Security }[] = [];
  for (const record of records) {
    const ranks: FieldRank[] = [];
    for (const term of terms) {
      const rank = bestFieldRank(record.fields, term);
      if (rank == null) {
        break;
      }
      ranks.push(rank);
    }
    if (ranks.length === terms.length) {
      matches.push({ ranks, security: record.security });
    }
  }

  return matches.toSorted((a, b) => compareAggregateRanks(a.ranks, b.ranks)).map(({ security }) => security);
}
